import React, {useEffect} from 'react';
import {
  AccessibilityInfo,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';

import Button from '/component/base/Button';
import Icon from '/component/base/Icon';
import BasePopover from '/component/base/Popover';
import TouchableOpacity from '/component/base/TouchableOpacity';
import Gutter from '/component/layout/Gutter';
import {UserProfileModal} from '/constant/TestIDs';
import {CLIENT_PROFILE} from '/enum/recordType.enum';
import {EVENT_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {withSafeArea} from '/provider/SafeAreaProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {generateCallback} from '/util/component.util';
import {withHOC} from '/util/hoc.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import ClientInfo from './component/ClientInfo';
import Volume from './component/Volume';
import {ALL_CONTENT} from './ClientReportPopover.constant';
import {styles} from './ClientReportPopover.style';
import {getPopoverContent, getStyles} from './ClientReportPopover.util';
interface ClientReportPopoverProps {
  lastPaidRank: string;
  tt: (key: string, defaultText?: string) => string;
  ts: (key: string, defaultText?: string) => string;
  safeAreaInsets: {bottom?: number};
  popoverClose: () => void;
  reportRecordData: {
    id?: string;
    firstName?: string;
    lastName?: string;
    [key: string]: any;
  };
  contentName: string;
  contentProps: Record<string, any>;
  onViewProfilePress: () => void;
  onOrderPress: () => void;
}

export const ClientReportPopover: React.FC<ClientReportPopoverProps> = ({
  lastPaidRank,
  tt = (key, fallback) => fallback ?? key,
  safeAreaInsets = {bottom: 0},
  popoverClose = () => {},
  reportRecordData = {} as ClientReportPopoverProps['reportRecordData'],
  contentName = '',
  contentProps = {},
  onViewProfilePress = () => {},
  onOrderPress = () => {},
}) => {
  const {width, height} = useWindowDimensions();
  const isLandscape = width > height;
  const isWeb = Platform.OS === 'web';
  const {id = '', firstName = '', lastName = ''} = reportRecordData;
  const {popoverStyle, footerButtonStyle} = getStyles(
    safeAreaInsets,
    isLandscape,
  );
  const PopoverContent = getPopoverContent(contentName, ALL_CONTENT);
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.profileClick,
  });
  const generateOnViewProfilePress = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.client,
      userId: id,
      userFullName: `${firstName} ${lastName}`,
      lastPaidRank: lastPaidRank,
    },
  });

  useEffect(() => {
    AccessibilityInfo.announceForAccessibility(
      `Client report opened for ${firstName} ${lastName}`,
    );
  }, [firstName, lastName]);

  return (
    <BasePopover
      style={popoverStyle}
      accessible={true}
      accessibilityRole="dialog"
      accessibilityLabel={`Client report for ${firstName} ${lastName}`}
      accessibilityViewIsModal={true}
      importantForAccessibility="yes">
      <View style={styles.closeButton}>
        <TouchableOpacity
          {...testProperties(null, true)}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={` ${firstName} ${lastName}.bottom sheet Double tap to close.`}
          onPress={popoverClose}>
          <Icon name="close" />
        </TouchableOpacity>
      </View>
      <Gutter>
        <ClientInfo
          accessible={true}
          accessibilityRole="text"
          accessibilityLabel={`${firstName} ${lastName} profile info`}
          reportRecordData={reportRecordData}
        />
      </Gutter>
      <ScrollView
        {...testProperties(UserProfileModal.reportPopoverScroll)}
        accessible={true}>
        <View {...testProperties(null, true)}>
          <PopoverContent
            {...contentProps}
            recordData={reportRecordData}
            onOrderPress={onOrderPress}
            accessible={true}
            accessibilityLabel="Report details"
          />
          <Gutter>
            <Volume
              reportRecordData={reportRecordData}
              accessible={true}
              accessibilityLabel="Client volume information"
            />
          </Gutter>
        </View>
      </ScrollView>
      <Gutter size={isLandscape ? 12 : 20}>
        <View style={[footerButtonStyle, {alignItems: 'center'}]}>
          <Button
            style={{
              ...(isLandscape
                ? {base: {width: '80%', paddingVertical: 16}}
                : {}),
              ...(isWeb
                ? {
                    base: {minHeight: 28, paddingHorizontal: 14},
                    text: {fontSize: 13},
                  }
                : {}),
            }}
            testID={UserProfileModal.viewProfile}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`View profile for ${firstName} ${lastName}`}
            onPress={() => {
              popoverClose(
                generateCallback({
                  func: onViewProfilePress,
                  params: {
                    recordType: CLIENT_PROFILE,
                    recordId: id,
                  },
                }),
                generateOnViewProfilePress(),
              );
            }}>
            {tt('footer.profileButton.label', 'View Profile')}
          </Button>
        </View>
      </Gutter>
    </BasePopover>
  );
};

export default withHOC(
  withSafeArea,
  withTranslation('reportCard.client'),
)(ClientReportPopover);
